import React, { useState, useEffect } from 'react';
import './CustomSearchModal.css';
import { useAuth } from '../../context/AuthContext.js';

const CustomSearchModal = ({ query, onClose, onResultClick, userId }) => {
  const { user, isAuthenticated, getToken } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState(null);

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = async (searchQuery) => {
    setLoading(true);
    setError(null);

    try {
      let token = getToken();

      // Check if user is authenticated
      if (!isAuthenticated() || !user) {
        console.warn('User not authenticated');
        throw new Error('Authentication required. Please log in to search.');
      }

      if (!token) {
        console.warn('No authentication token available');
        throw new Error('Authentication required. Please log in again.');
      }

      // Validate token format (basic check)
      if (!token.startsWith('eyJ')) {
        console.warn('Invalid token format detected');
        throw new Error('Invalid authentication token. Please log in again.');
      }

      console.log('Performing search with token:', token.substring(0, 20) + '...');

      // Use the correct API endpoint - the search endpoint already exists in aiRoute.js
      const response = await fetch(`http://localhost:5000/api/ai/search?q=${encodeURIComponent(searchQuery)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);

        if (response.status === 401) {
          console.warn('401 Unauthorized - Token might be expired');
          // Clear invalid token and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          throw new Error('Session expired. Please log in again.');
        } else if (response.status === 403) {
          console.warn('403 Forbidden - Access denied');
          throw new Error(errorData?.error || 'Access denied. Please check your permissions.');
        } else {
          console.error('Search failed with status:', response.status, errorData);
          throw new Error(errorData?.error || `Search failed with status: ${response.status}`);
        }
      }

      const data = await response.json();
      console.log('Search response received:', data);

      // Transform backend results to our format
      const searchResults = [];

      if (data.results && data.results.length > 0) {
        data.results.forEach((result, index) => {
          searchResults.push({
            id: result.id || `result-${index}`,
            title: result.title,
            description: result.description || result.title,
            url: result.url,
            type: result.type || 'search'
          });
        });
      }

      // If no results from backend, provide fallback
      if (searchResults.length === 0) {
        searchResults.push({
          id: 'fallback',
          title: `Search for "${searchQuery}"`,
          description: 'Click to search on Google',
          url: `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`,
          type: 'fallback'
        });
      }

      setResults(searchResults);
    } catch (err) {
      console.error('Search error:', err);

      // Enhanced error handling
      if (err.message.includes('Authentication required') || err.message.includes('Session expired')) {
        setError(err.message);
      } else if (err.message.includes('Network') || err.message.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Search failed. Please try again.');
      }

      // Fallback to Google search
      setResults([{
        id: 'fallback',
        title: `Search for "${searchQuery}"`,
        description: 'Click to search on Google',
        url: `https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`,
        type: 'fallback'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = async (result) => {
    // Track the click using userTrackingService
    try {
      // Import userTrackingService dynamically to avoid circular dependencies
      const { default: userTrackingService } = await import('../../services/userTrackingService.js');

      userTrackingService.trackEvent({
        eventType: 'search_result_click',
        resultData: {
          resultId: result.id,
          resultTitle: result.title,
          resultUrl: result.url,
          resultType: result.type,
          query: query
        },
        page: {
          url: window.location.href,
          title: document.title
        }
      });

      // Call the parent callback
      onResultClick(result);
    } catch (error) {
      console.error('Error tracking click:', error);
      // Still proceed with navigation
      onResultClick(result);
    }
  };

  return (
    <div className="custom-search-modal-overlay" onClick={onClose}>
      <div className="custom-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="custom-search-header">
          <h3>Search Results for "{query}"</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="custom-search-content">
          {loading && (
            <div className="loading">
              <div className="spinner"></div>
              <p>Searching...</p>
            </div>
          )}

          {error && (
            <div className="error">
              <p>{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="retry-button"
              >
                Refresh Page
              </button>
            </div>
          )}

          {!loading && !error && (
            <div className="search-results">
              {results.map((result) => (
                <div
                  key={result.id}
                  className="search-result-item"
                  onClick={() => handleResultClick(result)}
                >
                  <h4>{result.title}</h4>
                  <p>{result.description}</p>
                  <span className="result-url">{result.url}</span>
                  <span className="result-type">{result.type}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomSearchModal;
